import React, { useRef, useState } from 'react'
import { CButton, CButtonGroup, CCard, CCardBody, CCardFooter, CCol, CRow, CTooltip } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilInfo } from '@coreui/icons'
import { CChartLine } from '@coreui/react-chartjs'
import BotonCapturaChart from './BotonCapturaChart'
import BotonInfoChart from './BotonInfoChart'

// Formato universal: coma de miles (máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99").
const fmt = (n: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n)

// Formato MONEDA — máscara GX con símbolo configurable por empresa
// (EmpresaTipoMoneda — ver utils/moneda.ts)
import { fmtMoneda } from '../../utils/moneda'
import { opcionesEjes } from '../../utils/graficos'

// [RGM] 2026-10-05 (pedido Rafael — "Arqueo General de RUTAS"): el arqueo de
// la calle ya no muestra los totales del día (Cobrado/Prestado/Bases/Gastado)
// sino UNA FILA POR RUTA con el dinero del COBRADOR ASIGNADO a esa ruta:
//   totalRuta = base + abonos − renovaciones − gastos (calculatotalruta.cs:117).
// Verde para positivos y rojo para negativos (semántica financiera del arqueo).
const ARQUEO_COLOR_POSITIVO = '#1E9E6A' // verde success
const ARQUEO_COLOR_NEGATIVO = '#d64545'

// Fila del arqueo por ruta que manda /api/home/kpi (rutasArqueo).
interface ArqueoRuta {
  zona: number
  rutaNombre: string
  cobrador: string
  base: number
  abonos: number
  renovaciones: number
  gastos: number
  total: number
}

// Colores del arqueo (formato Cobrado/Prestado/Gastado/Bases Ruta de siempre
// — "Arqueo General de Rutas" con totales SOLO de los cobradores).
const ARQUEO_COLORS = {
  cobrado: '#1E9E6A',
  prestado: '#078bcd',
  bases: '#64748B',
  gastado: '#EC4899',
}

interface OperacionChartProps {
  kpi: {
    cobradoHoy: number
    prestadoHoy: number
    bases: number
    gastadoHoy: number
    utilidadBrutaMes: number
    // [RGM] 2026-10-05: arqueo por ruta + Σ (total en calle).
    rutasArqueo: ArqueoRuta[]
    totalRutasCalle: number
  }
  graf: {
    anio: number
    meses: string[]
    cobrado: number[]
    prestado: number[]
    gastado: number[]
    // [RGM] 2026-10-01: series de los botones Día/Mes (endpoint /home/graficas).
    diario?: { labels: string[]; cobrado: number[]; prestado: number[]; gastado: number[] }
    rolling?: { labels: string[]; cobrado: number[]; prestado: number[]; gastado: number[] }
  }
  // Totales del footer — [RGM] aprobado por Rafael (2026-08): Valor Negocio
  // = Total Cajas + Cartera Total (definición del panel, NO existe en la KB
  // GX — diseño nuevo firmado). Nuevos/Renovaciones del Mes y Total Cajas
  // son réplica (gethomesampledatacontrol.cs).
  footer?: {
    nuevosMes: number
    renovacionesMes: number
    totalCajas: number
    valorNegocio: number
  } | null
}

const OperacionChart: React.FC<OperacionChartProps> = ({ kpi, graf, footer }) => {
  // [RGM] Captura de la gráfica (botón descarga estilo CoreUI).
  const refChart = useRef<HTMLDivElement>(null)
  // [RGM] 2026-10-01 (pedido Rafael): período del chart — Día (últimos 30
  // días) / Mes (últimos 12 meses) / Año (meses del año). Antes Día y Mes
  // estaban deshabilitados.
  const [periodo, setPeriodo] = useState<'dia' | 'mes' | 'anio'>('anio')

  // Serie del período elegido (Día/Mes); Año usa las series mensuales.
  const seriePeriodo = periodo === 'dia' ? graf.diario : periodo === 'mes' ? graf.rolling : null
  const labelsPeriodo = seriePeriodo?.labels ?? graf.meses
  const datosPeriodo = {
    cobrado: seriePeriodo?.cobrado ?? graf.cobrado,
    prestado: seriePeriodo?.prestado ?? graf.prestado,
    gastado: seriePeriodo?.gastado ?? graf.gastado,
  }
  // Estructura SIEMPRE visible (aunque la empresa aún no tenga datos): las
  // series reales se dibujan planas en 0 cuando la base está vacía.
  const datasets = [
    { label: 'Cobrado', backgroundColor: 'rgba(7,139,205,0.1)', borderColor: '#078bcd', borderWidth: 2, fill: true, data: datosPeriodo.cobrado },
    // Verde más claro (#4CAF50): el #2E7D32 original se veía NEGRO en dark mode.
    { label: 'Prestado', backgroundColor: 'transparent', borderColor: '#4CAF50', borderWidth: 2, fill: false, data: datosPeriodo.prestado },
    { label: 'Gastado', backgroundColor: 'transparent', borderColor: '#d9534f', borderWidth: 2, borderDash: [8, 5], fill: false, data: datosPeriodo.gastado },
  ].filter((s) => s.data.length > 0)

  // Footer: Nuevos del Mes, Renovaciones del Mes, Utilidad recaudada Mes
  // (/api/home/kpi), Total Cajas (cajageneral.cs:959) y Valor Negocio
  // ([RGM] aprobado Rafael — Total Cajas + Cartera Total).
  // TODOS los items del footer son MONEDA (Nuevos/Renovaciones del Mes son
  // VALORES de dinero, no cantidades — pedido Rafael 2026-08-24).
  const footerItems = [
    { label: 'Nuevos', period: 'Mes', value: footer?.nuevosMes ?? 0 },
    { label: 'Renovaciones', period: 'Mes', value: footer?.renovacionesMes ?? 0 },
    { label: 'Utilidad recaudada', period: 'Mes', value: kpi.utilidadBrutaMes },
    // [RGM] 2026-10-01 (pedido Rafael): "Total Cajas" repetía "Total" (label +
    // período de abajo) → el label queda "Disponible Cajas" y abajo "Total".
    { label: 'Disponible Cajas', period: 'Total', value: footer?.totalCajas ?? 0 },
    { label: 'Valor Negocio', period: 'Total', value: footer?.valorNegocio ?? 0 },
  ]

  // Arqueo General de RUTAS — filas por ruta (dinero del cobrador asignado a
  // cada una) y "Total en calle" = Σ de los totales por ruta. Los datos viajan
  // en /api/home/kpi (rutasArqueo/totalRutasCalle).
  const rutasArqueo = kpi.rutasArqueo
  const totalRutasCalle = kpi.totalRutasCalle
  // [RGM] 2026-10-05 (pedido Rafael — "Arqueo General de Rutas"): MISMOS
  // totales del arqueo de siempre, pero SOLO de los cobradores (suma de lo
  // que hizo el cobrador asignado a cada ruta; Prestado = préstamos nuevos o
  // renovaciones de ese cobrador). Sin "Total en calle".
  const arqCobrado = rutasArqueo.reduce((s, r) => s + r.abonos, 0)
  const arqPrestado = rutasArqueo.reduce((s, r) => s + r.renovaciones, 0)
  const arqGastado = rutasArqueo.reduce((s, r) => s + r.gastos, 0)
  const arqBases = rutasArqueo.reduce((s, r) => s + r.base, 0)
  const arqGeneralRutas = totalRutasCalle
  const colorTotal = (valor: number) =>
    valor >= 0 ? ARQUEO_COLOR_POSITIVO : ARQUEO_COLOR_NEGATIVO

  return (
    <CCard className="mb-4">
      <CCardBody>
        <CRow>
          <CCol sm={5}>
            <h4 className="card-title mb-0">Operación del negocio</h4>
            <div className="small text-body-secondary">
              {periodo === 'dia'
                ? 'Últimos 30 días'
                : periodo === 'mes'
                  ? 'Últimos 12 meses'
                  : `Año ${graf.anio} — datos mensuales`}
            </div>
          </CCol>
          <CCol sm={7} className="d-none d-md-block">
            {/* Como el ejemplo oficial de CoreUI: botón de DESCARGA (nube ↓) a
                la derecha del título con el grupo Día/Mes/Año a su izquierda —
                en la réplica los botones SÍ funcionan (el ejemplo solo los pinta). */}
            <BotonCapturaChart contenedorRef={refChart} nombre="operacion-del-negocio" className="float-end" />
            {/* [RGM] 2026-10-01 (mejora aprobada — botón INFO ⓘ): describe el chart. */}
            <BotonInfoChart
              titulo="Operación del negocio"
              texto={
                <>
                  <div><strong>Cobrado</strong>, <strong>Prestado</strong> y <strong>Gastado</strong>: movimientos del período elegido (Día / Mes / Año).</div>
                  <div className="mt-1"><strong>Arqueo General de Rutas</strong>: es la suma total del dinero actual que tienen los cobradores en ruta (solo los movimientos del cobrador asignado a cada ruta).</div>
                </>
              }
              className="float-end"
            />
            <CButtonGroup className="float-end me-3">
              {/* [RGM] 2026-10-01 (pedido Rafael): tooltip al pasar el mouse —
                  que se entienda QUÉ muestra la gráfica con cada período. */}
              {(['dia', 'mes', 'anio'] as const).map((p) => (
                <CTooltip
                  key={p}
                  placement="top"
                  content={
                    p === 'dia'
                      ? 'Muestra los últimos 30 días (día a día)'
                      : p === 'mes'
                        ? 'Muestra los últimos 12 meses (mes a mes)'
                        : `Muestra los 12 meses del año ${graf.anio}`
                  }
                >
                  <CButton
                    color="outline-secondary"
                    size="sm"
                    className="mx-0"
                    active={periodo === p}
                    onClick={() => setPeriodo(p)}
                  >
                    {p === 'dia' ? 'Día' : p === 'mes' ? 'Mes' : 'Año'}
                  </CButton>
                </CTooltip>
              ))}
            </CButtonGroup>
          </CCol>
        </CRow>
        {/* Chart + Arqueo General del Día lado a lado (arqueo abajo en móvil).
            Arqueo réplica del móvil (dashboard_general_view.dart:70-129). */}
        <div className="row gx-4 align-items-center">
          <div className="col-12 col-md-8 col-lg-9 position-relative" ref={refChart}>
            <CChartLine
              style={{ height: '300px', marginTop: '40px' }}
              data={{
                labels: labelsPeriodo,
                datasets,
              }}
              options={{
                maintainAspectRatio: false,
                // [RGM] 2026-10-01 (pedido Rafael): leyenda con los colores de
                // las líneas DEBAJO del chart (se dibuja dentro del mismo canvas
                // de 300px — no cambia la altura de la tarjeta).
                plugins: {
                  legend: {
                    display: true,
                    position: 'bottom',
                    labels: { boxWidth: 12, font: { size: 11 } },
                  },
                },
                  scales: {
                    // Ejes con el color del TEMA (nunca negro en dark) + grilla
                    // gris neutra (utils/graficos.ts — opcionesEjes).
                    ...opcionesEjes().scales,
                    y: {
                      ...opcionesEjes().scales.y,
                      ticks: { ...opcionesEjes().scales.y.ticks, callback: (v: unknown) => fmt(Number(v)) },
                    },
                  },
                  elements: {
                    line: { tension: 0.4 },
                    point: { radius: 0, hitRadius: 10, hoverRadius: 4, hoverBorderWidth: 3 },
                  },
              }}
            />
          </div>
          <div className="col-12 col-md-4 col-lg-3">
            <CCard className="shadow-sm h-100">
              <CCardBody>
                <div className="small fw-bold text-body-secondary text-uppercase mb-1">Arqueo General de Rutas</div>
                {[
                  { titulo: 'Cobrado', valor: arqCobrado, color: ARQUEO_COLORS.cobrado },
                  { titulo: 'Prestado', valor: arqPrestado, color: ARQUEO_COLORS.prestado },
                  { titulo: 'Gastado', valor: arqGastado, color: ARQUEO_COLORS.gastado },
                  { titulo: 'Bases Ruta', valor: arqBases, color: ARQUEO_COLORS.bases },
                ].map((filaArq) => (
                  <div key={filaArq.titulo} className="d-flex justify-content-between py-1">
                    <span className="text-body-secondary">{filaArq.titulo}</span>
                    <span className="fw-semibold ms-3" style={{ color: filaArq.color }}>
                      {fmtMoneda(filaArq.valor)}
                    </span>
                  </div>
                ))}
                <hr className="my-1" />
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold d-inline-flex align-items-center gap-1">
                    Arqueo General
                    {/* [RGM] 2026-10-10 (pedido Rafael — "a la actual suma
                        agrégale esto: no incluye movimientos hechos desde el
                        admin"): el tooltip del título Arqueo General conserva
                        la descripción y suma la aclaración. */}
                    <CTooltip
                      placement="top"
                      content={
                        <div className="small" style={{ maxWidth: 300 }}>
                          Es la suma total del dinero actual que tienen los
                          cobradores en ruta. No incluye movimientos hechos
                          desde el admin.
                        </div>
                      }
                    >
                      <span style={{ cursor: 'help', lineHeight: 0 }}>
                        <CIcon icon={cilInfo} size="sm" className="text-body-secondary" />
                      </span>
                    </CTooltip>
                  </span>
                  <span className="fw-bold ms-3" style={{ color: colorTotal(arqGeneralRutas) }}>
                    {fmtMoneda(arqGeneralRutas)}
                  </span>
                </div>
                {/* [RGM] 2026-10-05 (texto EXACTO aprobado por Rafael): solo el
                    hecho — sin causas especuladas (el sistema cubre todos los
                    movimientos). */}
                {arqGeneralRutas < 0 && (
                  <div className="small text-danger mt-1">
                    En negativo: el disponible de los cobradores en ruta está por
                    debajo de cero — lo gastado supera la base de la ruta más los
                    cobros.
                  </div>
                )}
              </CCardBody>
            </CCard>
          </div>
        </div>
      </CCardBody>
      <CCardFooter>
        <CRow xs={{ cols: 1, gutter: 4 }} sm={{ cols: 2 }} lg={{ cols: 4 }} xl={{ cols: 5 }} className="mb-2 text-center">
          {footerItems.map((item) => (
            <CCol key={item.label}>
              <div className="text-body-secondary">{item.label}</div>
              <div className="small text-body-secondary">{item.period}</div>
              <div className="fw-semibold text-truncate">{fmtMoneda(item.value)}</div>
            </CCol>
          ))}
        </CRow>
      </CCardFooter>
    </CCard>
  )
}

export default OperacionChart
